import type { StudentEvent } from "@/services/api/user/events";
import {
  localized,
  parseEventDate,
  toTwentyFourHourClock,
  withMomentLocale,
} from "@/utils/eventFormat";

const CRLF = "\r\n";

/** Escape a value for an iCalendar text field: backslash, `;`, `,` and line breaks. */
export const escapeIcs = (value: string): string =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n/g, "\\n")
    .replace(/\r/g, "\\n")
    .replace(/\n/g, "\\n");

/**
 * Build a single-event VCALENDAR string (CRLF line endings) that iOS Safari
 * offers to "Add to Calendar" and Android can open with Google Calendar.
 * All-day events use `VALUE=DATE` with an exclusive end date; timed events use
 * floating local time (no `Z`).
 */
export const buildEventIcs = (event: StudentEvent, language: string): string => {
  const m = withMomentLocale("en");
  const details = event.details;
  const allDay = details.all_day === 0;

  const title =
    localized(details.title, details.title_en, language) || "Untitled Event";
  const location = localized(details.location, details.location_en, language);
  const description = localized(
    details.description,
    details.description_en,
    language,
  );

  const start = parseEventDate(details.start_date);
  const end = parseEventDate(details.end_date);

  let dtStart: string;
  let dtEnd: string;
  if (allDay) {
    const startDate = start ?? m();
    const endDate = end ?? startDate;
    dtStart = `DTSTART;VALUE=DATE:${startDate.format("YYYYMMDD")}`;
    dtEnd = `DTEND;VALUE=DATE:${endDate.clone().add(1, "day").format("YYYYMMDD")}`;
  } else {
    const startDate = start ?? m();
    const endDate = end ?? startDate;
    const startTime = toTwentyFourHourClock(details.start_time) || "00:00";
    const endTime = toTwentyFourHourClock(details.end_time) || startTime;
    const startDateTime = startDate.clone().set({
      hour: Number(startTime.split(":")[0]),
      minute: Number(startTime.split(":")[1]),
      second: 0,
    });
    const endDateTime = endDate.clone().set({
      hour: Number(endTime.split(":")[0]),
      minute: Number(endTime.split(":")[1]),
      second: 0,
    });
    dtStart = `DTSTART:${startDateTime.format("YYYYMMDDTHHmmss")}`;
    dtEnd = `DTEND:${endDateTime.format("YYYYMMDDTHHmmss")}`;
  }

  const organizerNames = event.organizers
    .map((organizer) => organizer.name_zh_hant || organizer.name_en || "")
    .filter(Boolean)
    .join(", ");
  const descriptionParts = [description, organizerNames, details.source_url]
    .filter(Boolean)
    .join("\n");

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Uninfo HK//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${details.id}@uninfohk`,
    `DTSTAMP:${m().utc().format("YYYYMMDDTHHmmss")}Z`,
    `SUMMARY:${escapeIcs(title)}`,
    ...(allDay ? ["X-MICROSOFT-CDO-ALLDAYEVENT:TRUE"] : []),
    dtStart,
    dtEnd,
    ...(location ? [`LOCATION:${escapeIcs(location)}`] : []),
    ...(descriptionParts ? [`DESCRIPTION:${escapeIcs(descriptionParts)}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return lines.join(CRLF) + CRLF;
};

/** Trigger a browser download of the event as an `.ics` file. */
export const downloadEventIcs = (event: StudentEvent, language: string): void => {
  const ics = buildEventIcs(event, language);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `uninfohk-${event.details.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};